import type { Account } from '@/types/models';

/**
 * Shared "sort accounts by street number" logic. Association account codes look
 * like "1147AO", "998AB", "1083OB" — a street number with a building/section
 * suffix. Sorting the raw string is wrong ("1083" < "998" lexically), so every
 * account list that wants door-order sorting routes through here.
 *
 * Two encodings, same intent:
 *   - `compareByStreetNumber` — an Array.sort comparator (used to pre-sort the
 *     rows we hand to a table / picker).
 *   - `streetSortKey` — a single zero-padded composite string whose lexical
 *     order equals numeric-then-suffix order (used as a DataTable column
 *     `sortAccessor`, which must return one primitive).
 */

/** Leading integer of an account's code/address, or null when there's no numeric prefix. */
export const accountStreetNumber = (a: Account): number | null => {
	const m = (a.accountNumber || a.address || '').trim().match(/^\s*(\d+)/);
	return m ? Number(m[1]) : null;
};

const parseCodeKey = (src: string): { num: number; suffix: string } => {
	const s = (src || '').trim();
	const m = s.match(/^\s*(\d+)\s*(.*)$/);
	return { num: m ? Number(m[1]) : Number.POSITIVE_INFINITY, suffix: (m ? m[2] : s).toUpperCase() };
};

const parseStreetKey = (a: Account): { num: number; suffix: string } =>
	parseCodeKey(a.accountNumber || a.address || '');

/**
 * Compare two raw account-code/address strings by numeric street number then
 * suffix — the string-level primitive behind `compareByStreetNumber`. Use when
 * you only have the code (e.g. a preview row, not a full Account).
 */
export const compareAccountCodes = (a: string, b: string): number => {
	const ka = parseCodeKey(a);
	const kb = parseCodeKey(b);
	if (ka.num !== kb.num) return ka.num - kb.num;
	return ka.suffix.localeCompare(kb.suffix);
};

/**
 * Compare two accounts by NUMERIC street number (ascending), so "998AB" sorts
 * before "1083OB". Accounts with no leading number sort last; ties on the
 * number fall back to the alpha suffix.
 */
export const compareByStreetNumber = (a: Account, b: Account): number => {
	const ka = parseStreetKey(a);
	const kb = parseStreetKey(b);
	if (ka.num !== kb.num) return ka.num - kb.num;
	return ka.suffix.localeCompare(kb.suffix);
};

/**
 * Single comparable key for a DataTable column `sortAccessor`. The number is
 * zero-padded to a fixed width so string comparison matches numeric order, then
 * the suffix is appended for the tiebreak. Numberless codes get a high sentinel
 * so they sort last (matching `compareByStreetNumber`).
 */
export const streetSortKey = (a: Account): string => {
	const { num, suffix } = parseStreetKey(a);
	const numKey = Number.isFinite(num) ? String(num).padStart(8, '0') : '99999999';
	return `${numKey}${suffix}`;
};
